import { ITEM_TYPES, type Condition, type ItemType } from '@brickloop/core/schemas';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../api';
import { TYPE_LABEL } from '../lib/labels';
import { resolveColor, usesColor } from '../lib/purchaseOrderForm';
import { useColors } from '../queries';
import { Button, Card, ErrorMessage, Field, Input, Notice, Select, number, plural } from '../ui';

interface Out {
  key: number;
  itemType: ItemType;
  itemNo: string;
  colorText: string;
  condition: Condition;
  qty: string;
  name: string;
}
let nextKey = 1;
const blank = (condition: Condition): Out => ({ key: nextKey++, itemType: 'MINIFIG', itemNo: '', colorText: '', condition, qty: '1', name: '' });
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Swaps some of this stock for different stock: a mystery box opened, a bag that held something else, the wrong
 * item sent. What comes out is costed at what the stock that went in cost.
 */
export function OpenStockCard({ lotId, itemNo, condition, onHand }: { lotId: number; itemNo: string; condition: Condition; onHand: number }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const colors = useColors();
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState('1');
  const [openedOn, setOpenedOn] = useState(today());
  const [method, setMethod] = useState<'by_value' | 'equal'>('by_value');
  const [outs, setOuts] = useState<Out[]>([blank(condition)]);
  const set = (key: number, patch: Partial<Out>) => setOuts((o) => o.map((x) => (x.key === key ? { ...x, ...patch } : x)));

  const problems: string[] = [];
  const qtyN = Number(qty);
  if (!Number.isInteger(qtyN) || qtyN < 1 || qtyN > onHand) problems.push(`Take out between 1 and ${number(onHand)}.`);
  const rows = outs.map((o) => ({ o, color: resolveColor(o.colorText, o.itemType, colors.data) }));
  for (const { o, color } of rows) {
    const label = o.itemNo.trim() || 'A line';
    if (!o.itemNo.trim()) problems.push('Every line needs an item number.');
    else if (!color) problems.push(`${label}: choose a colour.`);
    if (!Number.isInteger(Number(o.qty)) || Number(o.qty) < 1) problems.push(`${label}: the quantity must be a whole number, 1 or more.`);
  }

  const run = useMutation({
    mutationFn: () =>
      api<{ result: { purchaseId: number } }>(`/api/v1/lots/${lotId}/open`, {
        body: {
          qty: qtyN,
          openedOn,
          method,
          outputs: rows.map(({ o, color }) => ({
            itemType: o.itemType,
            itemNo: o.itemNo.trim(),
            colorId: color!.id,
            condition: o.condition,
            qty: Number(o.qty),
            ...(o.name.trim() && { name: o.name.trim() }),
          })),
        },
      }).then((r) => r.result),
    onSuccess: (r) => {
      for (const k of ['lot', 'lots', 'dashboard', 'purchases', 'purchase', 'items', 'item-inventory', 'item-movements', 'item-profitability']) void qc.invalidateQueries({ queryKey: [k] });
      navigate(`/purchases/${r.purchaseId}`);
    },
  });

  if (!open) {
    return (
      <Card title="Open or swap" className="lg:col-span-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-600">
            Opened a mystery box, or got something different from what you bought? Swap this stock for what was really inside. It carries the cost over.
          </p>
          <Button variant="secondary" onClick={() => setOpen(true)}>
            Open or swap {itemNo}…
          </Button>
        </div>
      </Card>
    );
  }
  return (
    <Card title="Open or swap" className="lg:col-span-3">
      <datalist id="open-color-names">
        {(colors.data ?? [])
          .filter((c) => c.id !== 0)
          .map((c) => (
            <option key={c.id} value={c.name} />
          ))}
      </datalist>
      <p className="mb-3 text-sm text-slate-600">
        Take {itemNo} out of stock and bring in what was actually there. What comes in is costed at what the {itemNo} cost, shared by value if there is more than one thing. It is dated, recorded as its own entry under Purchases, and shows in the stock history.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <Field label={`How many ${itemNo} (of ${number(onHand)})`} className="w-48">
          <Input inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} />
        </Field>
        <Field label="Date" className="w-44">
          <Input type="date" value={openedOn} onChange={(e) => setOpenedOn(e.target.value)} />
        </Field>
        {outs.length > 1 && (
          <Field label="Share the cost" className="w-64">
            <Select value={method} onChange={(e) => setMethod(e.target.value as typeof method)}>
              <option value="by_value">By value, using prices you have set</option>
              <option value="equal">Equally per piece</option>
            </Select>
          </Field>
        )}
      </div>
      <h3 className="mt-4 text-sm font-medium">What came out</h3>
      <div className="overflow-x-auto">
        <table className="data w-full min-w-[46rem] text-sm">
          <thead>
            <tr>
              <th>Type</th>
              <th>Item number</th>
              <th>Colour</th>
              <th>Condition</th>
              <th className="text-right">Quantity</th>
              <th>Name, if BrickLink does not give it</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {outs.map((o) => (
              <tr key={o.key}>
                <td>
                  <Select aria-label="Item type" className="w-28" value={o.itemType} onChange={(e) => set(o.key, { itemType: e.target.value as ItemType })}>
                    {ITEM_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {TYPE_LABEL[t]}
                      </option>
                    ))}
                  </Select>
                </td>
                <td>
                  <Input aria-label="Item number" className="w-32" autoComplete="off" value={o.itemNo} onChange={(e) => set(o.key, { itemNo: e.target.value })} />
                </td>
                <td>
                  <Input
                    aria-label="Colour"
                    className="w-40"
                    list="open-color-names"
                    disabled={!usesColor(o.itemType)}
                    placeholder={usesColor(o.itemType) ? 'Type a colour' : 'not applicable'}
                    value={usesColor(o.itemType) ? o.colorText : ''}
                    onChange={(e) => set(o.key, { colorText: e.target.value })}
                  />
                </td>
                <td>
                  <Select aria-label="Condition" className="w-24 min-w-24" value={o.condition} onChange={(e) => set(o.key, { condition: e.target.value as Condition })}>
                    <option value="N">New</option>
                    <option value="U">Used</option>
                  </Select>
                </td>
                <td>
                  <Input aria-label="Quantity" inputMode="numeric" className="w-20 text-right" value={o.qty} onChange={(e) => set(o.key, { qty: e.target.value })} />
                </td>
                <td>
                  <Input aria-label="Name if the item is new" className="w-52" value={o.name} onChange={(e) => set(o.key, { name: e.target.value })} />
                </td>
                <td>
                  <Button variant="ghost" small disabled={outs.length === 1} onClick={() => setOuts((x) => x.filter((r) => r.key !== o.key))}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2">
        <Button variant="ghost" onClick={() => setOuts((x) => [...x, blank(condition)])}>
          Add another thing that came out
        </Button>
      </div>
      {problems.length > 0 && (
        <Notice tone="amber">
          <ul className="list-disc pl-5">
            {[...new Set(problems)].map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Notice>
      )}
      <ErrorMessage error={run.error} />
      <div className="mt-3 flex gap-2">
        <Button disabled={problems.length > 0 || run.isPending} onClick={() => run.mutate()}>
          {run.isPending ? 'Swapping…' : `Take out ${plural(qtyN || 0, itemNo)} and bring in what came out`}
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}
